import { APPROVAL_LABELS, canDecideApproval, type Actor, type ApprovalRequest, type TenantUser } from '@titan/shared';
import { useState } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import { Actions, Card, ConfirmButton, InlineError, Muted, Pill, PillRow } from '../../ui/index.js';
import { errorMessage } from '../useLoad.js';
import styles from './Access.module.css';

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Pending sensitive changes. The requester sees theirs waiting; an eligible second person
 * approves or rejects with the impact in front of them.
 */
export function ApprovalsCard({ approvals, actor, users, onChanged }: { approvals: ApprovalRequest[]; actor: Actor; users: TenantUser[]; onChanged: () => Promise<void> }) {
  const { api } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (approvals.length === 0) return null;

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      await api(`/approvals/${id}/${decision}`, { method: 'POST', body: JSON.stringify({}) });
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="warning" title={`${approvals.length} ${approvals.length === 1 ? 'change waits' : 'changes wait'} for a second person`} description="Sensitive changes apply only after another superadmin or DinoCloud approves them. Requests expire after 72 hours.">
      {error && <InlineError>{error}</InlineError>}
      {approvals.map((r) => {
        const mine = r.requestedBy === actor.email;
        const can = canDecideApproval(actor, r, users);
        return (
          <div key={r.id} className={styles.detailBlock}>
            <PillRow>
              <Pill tone="warning">{APPROVAL_LABELS[r.kind]}</Pill>
              <Muted small>
                Asked by {mine ? 'you' : `${r.requestedByName} (${r.requestedBy})`} · {when(r.requestedAt)} · expires {when(r.expiresAt)}
              </Muted>
            </PillRow>
            <span>{r.summary}</span>
            {can ? (
              <Actions>
                <ConfirmButton label="Approve" variant="primary" size="sm" disabled={busy} message={`Approving applies this now: ${r.summary}`} confirmLabel="Approve and apply" onConfirm={() => decide(r.id, 'approve')} />
                <ConfirmButton label="Reject" size="sm" tone="critical" disabled={busy} message="Rejecting closes the request. Nothing changes; the requester is told." confirmLabel="Reject" onConfirm={() => decide(r.id, 'reject')} />
              </Actions>
            ) : (
              <Muted small>{mine ? 'Waiting for another superadmin or DinoCloud.' : 'Only a different superadmin or DinoCloud can decide this one.'}</Muted>
            )}
          </div>
        );
      })}
    </Card>
  );
}
